import cartIcon from "@assets/images/cart-white.svg";
import CloseIcon from "@assets/images/CloseModal.svg";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import NumberFormat from "@saleor/components/NumberFormat";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { DrawAddExportType } from "@saleor/createOrderExport/const";
import { DataCartDetailProps } from "@saleor/createOrderExport/views/CreateOrderView";
import { useExportPiGetAllProductsQuery } from "@saleor/graphql";
import { renderCollection } from "@saleor/misc";
import { convertToRolExport } from "@saleor/utils/data/convertUnit";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import DialogError from "../DialogError";
import { useStyles } from "./styles";

interface DrawAddProductProps {
  onClose?: () => void;
  open: boolean;
  contractId?: string;
  refetchData?: () => void;
  data: DataCartDetailProps;
  handleSubmit?: (value: any) => void;
  title?: string;
  listProductAdded?: any[];
  addType: DrawAddExportType;
}

const DrawAddProduct: React.FC<DrawAddProductProps> = props => {
  const {
    onClose,
    open,
    data,
    contractId,
    handleSubmit,
    title,
    listProductAdded,
    addType
  } = props;
  const classes = useStyles();
  const intl = useIntl();
  const [listLineState, setListLineState] = useState<any[]>([]);
  const [isError, setError] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const {
    data: dataFetchMaterialItems,
    loading: loadingFetchMatetialItems
  } = useExportPiGetAllProductsQuery({
    displayLoader: true,
    variables: { id: contractId },
    skip: !open
  });

  const onClickCheckHeader = () => {
    const list = [...listLineState];
    const listSelected = getSelectedLine();
    if (listSelected.length < listLineState.length) {
      list.forEach(item => {
        item.selected = true;
        //
        let quantityAdded = 0;
        const limit =
          item.salesUnit === "EA"
            ? item.remainingQuantityEx
            : item.remainingQuantityEx / item.weight;
        if (addType === DrawAddExportType.CHANGEORDER) {
          const itemAdded = listProductAdded
            ? listProductAdded.filter(
                added => added.material === item.product.name && added.isAddNew
              )
            : [];
          quantityAdded = itemAdded.reduce(
            (sum, itemSum) => parseInt(itemSum.quantity, 10) + sum,
            0
          );
        } else {
          const itemAdded = listProductAdded
            ? listProductAdded.filter(added => added.piProduct?.id === item?.id)
            : [];
          quantityAdded = itemAdded.reduce(
            (sum, itemSum) => parseInt(itemSum.quantity, 10) + sum,
            0
          );
        }

        const totalItem = parseInt(item.quantity, 10) + quantityAdded;
        //
        if (item.quantity) {
          item.errorQuantity = totalItem > limit;
        } else {
          item.errorQuantity = true;
        }
      });
    } else if (listSelected.length === listLineState.length) {
      list.forEach(item => {
        item.selected = false;
        item.errorQuantity = false;
      });
    }
    setListLineState(list);
  };

  const onChangeCheckItem = (event, lineId) => {
    const checked = event.target.checked;
    const list = [...listLineState];
    const lineChange = list.find(item => item.id === lineId);
    lineChange.selected = checked;
    //
    let quantityAdded = 0;
    const limit =
      lineChange.salesUnit === "EA"
        ? lineChange.remainingQuantityEx
        : lineChange.remainingQuantityEx / lineChange.weight;

    if (addType === DrawAddExportType.CHANGEORDER) {
      const itemAdded = listProductAdded
        ? listProductAdded.filter(
            added =>
              added.material === lineChange.product.name && added.isAddNew
          )
        : [];
      quantityAdded = itemAdded.reduce(
        (sum, itemSum) => parseInt(itemSum.quantity, 10) + sum,
        0
      );
    } else {
      const itemAdded = listProductAdded
        ? listProductAdded.filter(
            added => added.piProduct?.id === lineChange?.id
          )
        : [];
      quantityAdded = itemAdded.reduce(
        (sum, itemSum) => parseInt(itemSum.quantity, 10) + sum,
        0
      );
    }

    const totalItem = parseInt(lineChange.quantity, 10) + quantityAdded;
    //
    if (!lineChange.quantity) {
      lineChange.errorQuantity = checked;
    } else {
      if (totalItem > limit) {
        lineChange.errorQuantity = lineChange.selected;
      }
    }
    setListLineState(list);
  };

  const handleChangeQuantity = (event, lineId) => {
    const value = event.target.value;
    const list = [...listLineState];
    const lineChange = list.find(item => item.id === lineId);
    lineChange.quantity = value;
    lineChange.selected = !!value;
    //

    let quantityAdded = 0;
    const limit =
      lineChange.salesUnit === "EA"
        ? lineChange.remainingQuantityEx
        : lineChange.remainingQuantityEx / lineChange.weight;

    if (addType === DrawAddExportType.CHANGEORDER) {
      const itemAdded = listProductAdded
        ? listProductAdded.filter(
            added =>
              added.material === lineChange.product.name && added.isAddNew
          )
        : [];
      quantityAdded = itemAdded.reduce(
        (sum, itemSum) => parseInt(itemSum.quantity, 10) + sum,
        0
      );
    } else {
      const itemAdded = listProductAdded
        ? listProductAdded.filter(
            added => added.piProduct?.id === lineChange?.id
          )
        : [];
      quantityAdded = itemAdded.reduce(
        (sum, itemSum) => parseInt(itemSum.quantity, 10) + sum,
        0
      );
    }

    const totalItem = parseInt(value, 10) + quantityAdded;
    //
    lineChange.errorQuantity = !value ? false : totalItem > limit;
    setListLineState(list);
  };

  const getSelectedLine = () => listLineState.filter(item => item.selected);

  const getCheckedHeader = () => listLineState.every(item => item.selected);

  const getIndeterminateHeader = () => {
    const listSelectedLength = getSelectedLine().length;
    return listSelectedLength > 0 && listSelectedLength < listLineState.length;
  };
  const header = (
    <div className={classes.titleWrapper}>
      <MyTypography variant="h5" className={classes.title}>
        {intl.formatMessage({ defaultMessage: "เพิ่มรายการสินค้า" })}
      </MyTypography>
      <div className={classes.headerClose}>
        <MyTypography variant={"caption"}>
          {intl.formatMessage({ defaultMessage: "ปิดหน้าต่าง" })}
        </MyTypography>
        <button id="close-popup-add-item" onClick={() => onClose()}>
          <img src={CloseIcon} alt="close-icon" />
        </button>
      </div>
    </div>
  );

  useEffect(() => {
    const data = dataFetchMaterialItems?.exportPi?.allPiProducts;
    if (data && !loadingFetchMatetialItems) {
      if (data?.length > 0) {
        const listItem = data?.map(item => ({
          ...item,
          selected: false,
          errorQuantity: false
        }));
        setListLineState(listItem);
      }
    }
  }, [
    dataFetchMaterialItems?.exportPi?.allPiProducts,
    loadingFetchMatetialItems
  ]);

  const renderError = line => {
    if (line.errorQuantity) {
      if (line.quantity) {
        return "จำนวนที่สั่งมีมากกว่าจำนวนคงเหลือ";
      } else {
        return "โปรดระบุจำนวนที่ต้องการ";
      }
    }
  };

  const handleAddtoCart = () => {
    const listSelected = getSelectedLine();

    const getErrorLine = () => listSelected.some(item => item.errorQuantity);
    if (getErrorLine()) {
      setError(true);
    } else {
      setError(false);
      const newListSelected = listSelected.map(e => ({ ...e, isNew: true }));
      handleSubmit(newListSelected);
    }
  };

  const handleClear = () => {
    const list = [...listLineState];
    const lineChange = list.map(item => ({
      ...item,
      quantity: "",
      selected: false,
      errorQuantity: false
    }));

    setListLineState(lineChange);
  };

  return (
    <>
      <Drawer
        onClose={onClose}
        open={open}
        anchor={"bottom"}
        PaperProps={{
          className: classes.wrapper
        }}
        data-testid="drawer-add-product"
      >
        {header}

        <Grid container>
          <Grid item xs={12} md={1}>
            <Box className={classes.gridTitle}>
              <MyTypography variant="subtitle3">
                {intl.formatMessage({ defaultMessage: "PI No." })}
              </MyTypography>
              <MyTypography variant="body2">{data?.piNo}</MyTypography>
            </Box>
          </Grid>
          <Grid item xs={12} md={11}>
            <Box className={classes.gridTitle}>
              <MyTypography variant="subtitle3">
                {intl.formatMessage({ defaultMessage: "Sold to" })}
              </MyTypography>
              <MyTypography variant="body2">
                {`${dataFetchMaterialItems?.exportPi?.soldTo?.code ||
                  ""}-${dataFetchMaterialItems?.exportPi?.soldTo?.name || ""}`}
              </MyTypography>
            </Box>
          </Grid>
        </Grid>
        <div className={classes.root} ref={containerRef}>
          <Table className={classes.mainTable} data-testid="add-product-table">
            <TableHead>
              <TableRow>
                <TableCellHeader style={{ minWidth: 50 }}>
                  <Checkbox
                    size={"small"}
                    checked={getCheckedHeader()}
                    indeterminate={getIndeterminateHeader()}
                    onClick={onClickCheckHeader}
                    name="checkbox-header"
                    id="checkbox-header"
                    data-testid="checkbox-header"
                  />
                </TableCellHeader>
                <TableCellHeader style={{ minWidth: 400 }}>
                  <FormattedMessage defaultMessage={"รายละเอียดสินค้า"} />
                </TableCellHeader>
                <TableCellHeader style={{ minWidth: 220 }} textAlign="left">
                  <FormattedMessage defaultMessage={"จำนวนสั่ง"} />
                </TableCellHeader>
                <TableCellHeader style={{ minWidth: 100 }}>
                  <FormattedMessage defaultMessage={"หน่วย"} />
                </TableCellHeader>
                <TableCellHeader
                  textAlign="right"
                  className={classes.cellBackGround}
                >
                  <MyTypography
                    variant={"subtitle3"}
                    className={classes.normalText}
                  >
                    {intl.formatMessage({ defaultMessage: "จำนวน" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  textAlign="right"
                  className={classes.cellBackGround}
                >
                  <MyTypography
                    variant={"subtitle3"}
                    className={classes.normalText}
                  >
                    {intl.formatMessage({ defaultMessage: "คงเหลือ" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  textAlign={"center"}
                  className={classes.cellBackGround}
                >
                  <MyTypography
                    variant={"subtitle3"}
                    className={classes.normalText}
                  >
                    {intl.formatMessage({ defaultMessage: "หน่วย" })}
                  </MyTypography>
                </TableCellHeader>
                <TableCellHeader
                  textAlign="right"
                  className={classNames(
                    classes.cellBackGround,
                    classes.lastCol
                  )}
                >
                  <MyTypography
                    variant={"subtitle3"}
                    className={classes.normalText}
                  >
                    {intl.formatMessage({ defaultMessage: "ราคา/หน่วย" })}
                  </MyTypography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderCollection(listLineState, item => (
                <TableRow className={classes.tableRow} key={item.id}>
                  <TableCell>
                    <Checkbox
                      size={"small"}
                      checked={item.selected}
                      onChange={e => onChangeCheckItem(e, item.id)}
                      id={`checkbox-${item.id}`}
                    />
                  </TableCell>
                  <TableCell className={classes.colNumber}>
                    {item ? item?.product?.name : <Skeleton />}
                  </TableCell>
                  <TableCell>
                    {item ? (
                      <NumberFormat
                        placeholder={"ระบุจำนวน"}
                        onChange={e => handleChangeQuantity(e, item.id)}
                        value={item?.quantity}
                        error={item.errorQuantity}
                        decimalScale={0}
                        allowLeadingZeros
                        id={`quantity-${item.id}`}
                        testId="quantity-item-input"
                      />
                    ) : (
                      <Skeleton />
                    )}
                    <div className={classes.error}>{renderError(item)}</div>
                  </TableCell>
                  <TableCell className={classes.colProductName}>
                    {item ? convertToRolExport(item?.salesUnit) : <Skeleton />}
                  </TableCell>
                  <TableCell align="right" className={classes.cellBackGround}>
                    <DecimalFormat
                      value={item.totalQuantity}
                      isGrayDecimal
                      countDecimal={3}
                    />
                  </TableCell>
                  <TableCell align="right" className={classes.cellBackGround}>
                    <DecimalFormat
                      value={item.remainingQuantity}
                      isGrayDecimal
                      countDecimal={3}
                    />
                  </TableCell>
                  <TableCell
                    align={"center"}
                    className={classes.cellBackGround}
                  >
                    <MyTypography variant={"body3"}>
                      {item.weightUnit}
                    </MyTypography>
                  </TableCell>
                  <TableCell
                    align="right"
                    className={classNames(
                      classes.cellBackGround,
                      classes.lastCol
                    )}
                  >
                    {item.pricePerUnit ? (
                      <DecimalFormat
                        value={item.pricePerUnit}
                        isGrayDecimal={false}
                        countDecimal={2}
                      />
                    ) : (
                      ""
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {listLineState?.length ? (
            <StickyHorizontalScrollBar container={containerRef} />
          ) : null}
        </div>

        <div className={classes.btnContainer}>
          <MyTypography variant="body2">
            เลือก <b>{getSelectedLine().length}</b> รายการ
          </MyTypography>

          <div className={classes.rightContainer}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClear}
              disabled={!getSelectedLine().length}
              id="clear-button"
              name="clear-button"
              data-testid="clear-button"
            >
              {intl.formatMessage({ defaultMessage: "Clear" })}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!getSelectedLine().length}
              onClick={handleAddtoCart}
              startIcon={<SVG src={cartIcon} />}
              name="add-to-cart-button"
              id="add-to-cart-button"
              data-testid="add-to-cart-button"
            >
              {title}
            </Button>
          </div>
        </div>
      </Drawer>
      <DialogError
        onClose={() => setError(false)}
        open={isError}
        testId="add-product-error-modal"
      />
    </>
  );
};

export default DrawAddProduct;
